import React, { useState, useEffect, useRef } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { LOOTBOX_RARITIES, S2_LOOTBOX_PRICES, S2_IMPOSTOR_LOOTBOX_PRICES, S2_FREE_DAILY_BOXES, S2_ITEM_VALUES, S2_MODIFIER_MULTIPLIERS, getLootboxItems, getLootboxRarities } from '../../constants/lootbox';
import { VFX_EFFECTS } from '../../constants/vfx';
import LootboxAnimation from './LootboxAnimation';
import LootboxRewardModal from './LootboxRewardModal';
import NuclearExplosionOverlay from './NuclearExplosionOverlay';
import ListingModal from './ListingModal';
import TradeUpSection from './TradeUpSection';
import CollectionTracker from './CollectionTracker';
import LootboxInventory from './LootboxInventory';
import ItemIcon from './ItemIcon';
import { useToast } from '../ToastContext';
import { PgHeader, SubTabs } from '../ui';

const LB_TABS = [
  { id: 'open',       label: '🎁 Loot Box' },
  { id: 'shop',       label: '🛒 Shop' },
  { id: 'inventory',  label: '🎒 Inventory' },
  { id: 'tradeup',    label: '⬆️ Trade Up' },
  { id: 'collection', label: '📊 Collection' },
];
function LootboxPage({ user, userData, onUserDataChange, seasonConfig }) {
  const toast = useToast();
  const isS2 = true;

  const shopPrices = Object.fromEntries(
    Object.entries(S2_LOOTBOX_PRICES).map(([type, cp]) => {
      const ip = S2_IMPOSTOR_LOOTBOX_PRICES[type] || {};
      const entry = {};
      if (cp.single) entry[1] = { crew: cp.single, ...(ip.single ? { imp: ip.single } : {}) };
      if (cp.bulk) entry[cp.bulkCount] = { crew: cp.bulk, ...(ip.bulk ? { imp: ip.bulk } : {}) };
      return [type, entry];
    })
  );
  const freeDailyBoxes = S2_FREE_DAILY_BOXES;

  const [isOpening, setIsOpening] = useState(false);
  const isOpeningRef = useRef(false); // ref-based guard to prevent race conditions from rapid clicks
  const [wonItem, setWonItem] = useState(null);
  const [showReward, setShowReward] = useState(false);
  const [canClaimDaily, setCanClaimDaily] = useState(false);
  const [nextClaimTime, setNextClaimTime] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [selectedTab, setSelectedTab] = useState("open"); // "open", "shop", "inventory", or "tradeup"
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [currentLootboxType, setCurrentLootboxType] = useState('normal');
  const [showListingModal, setShowListingModal] = useState(false); // For selling duplicate items
  const [itemToList, setItemToList] = useState(null); // Item to be listed from duplicate
  const [showNuclearExplosion, setShowNuclearExplosion] = useState(false); // Fullscreen nuclear meltdown
  const [showWorthInfo, setShowWorthInfo] = useState(false);
  // Local optimistic state overlay — overrides prop values until server confirms
  const [localUserData, setLocalUserData] = useState(null);
  // Merge prop + local overlay; local takes precedence for optimistic updates
  const effectiveUserData = localUserData ? { ...userData, ...localUserData } : userData;
  // Helper: apply optimistic patch and auto-clear after server refresh
  function setUserData(updater) {
    setLocalUserData(prev => {
      const base = prev || {};
      return typeof updater === 'function' ? updater(base) : { ...base, ...updater };
    });
  }

  // Check if user can claim free daily lootboxes (separate from weekly rank rewards)
  useEffect(() => {
    if (!user || !effectiveUserData) {
      setCanClaimDaily(false);
      return;
    }

    const now = Date.now();
    const lastClaim = effectiveUserData.lastFreeLootbox || 0;
    const hoursSinceLastClaim = (now - lastClaim) / (1000 * 60 * 60);

    if (hoursSinceLastClaim >= 24) {
      setCanClaimDaily(true);
      setNextClaimTime(null);
    } else {
      setCanClaimDaily(false);
      const nextClaimTime = new Date(lastClaim + (24 * 60 * 60 * 1000));
      setNextClaimTime(nextClaimTime);
    }
  }, [user, effectiveUserData]);

  // Load user inventory
  useEffect(() => {
    if (effectiveUserData && effectiveUserData.lootboxInventory) {
      setInventory(effectiveUserData.lootboxInventory);
    }
  }, [effectiveUserData]);

  // Sound effect functions using Web Audio API
  function playOpenSound() {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.3);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  }

  function playScrollSound() {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // CSS easing: cubic-bezier(0.25, 0.46, 0.45, 0.94)
    // This maps normalized time (0-1) to normalized progress (0-1)
    const cubicBezier = (t, p1x, p1y, p2x, p2y) => {
      // eslint-disable-next-line no-unused-vars
      const cx = 3 * p1x;
      // eslint-disable-next-line no-unused-vars
      const bx = 3 * (p2x - p1x) - cx;
      // eslint-disable-next-line no-unused-vars
      const ax = 1 - cx - bx;

      const cy = 3 * p1y;
      const by = 3 * (p2y - p1y) - cy;
      const ay = 1 - cy - by;

      return ((ay * t + by) * t + cy) * t;
    };

    // Given a target progress (how far along the animation), find the time
    const findTimeForProgress = (targetProgress, p1x, p1y, p2x, p2y) => {
      // Binary search to find time t where cubicBezier(t) = targetProgress
      let low = 0;
      let high = 1;
      let mid = 0.5;

      for (let iteration = 0; iteration < 30; iteration++) {
        mid = (low + high) / 2;
        const currentProgress = cubicBezier(mid, p1x, p1y, p2x, p2y);

        if (Math.abs(currentProgress - targetProgress) < 0.00001) {
          break;
        }

        if (currentProgress < targetProgress) {
          low = mid;
        } else {
          high = mid;
        }
      }

      return mid;
    };

    const totalItemsInAnimation = 50; // Items 0-49 pass, item 50 stops at center
    const animationDuration = 5.0;
    const startDelay = 0.05; // Small buffer to sync with CSS animation start

    // Play a sound each time an item crosses the center indicator
    // We play 50 sounds for items 0-49 crossing, but NOT at progress=1.0 (which is after animation ends)
    for (let itemIndex = 0; itemIndex < totalItemsInAnimation; itemIndex++) {
      // Progress from 0 to ~0.98 as items 0-49 pass through center
      const itemProgress = itemIndex / totalItemsInAnimation;

      // Find the time (0-1) when this progress is reached with easing
      const normalizedTime = findTimeForProgress(itemProgress, 0.25, 0.46, 0.45, 0.94);

      // Convert to actual audio time with start delay
      const soundTime = audioContext.currentTime + startDelay + (normalizedTime * animationDuration);

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, soundTime);
      gainNode.gain.setValueAtTime(0.1, soundTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, soundTime + 0.05);

      oscillator.start(soundTime);
      oscillator.stop(soundTime + 0.05);
    }
  }

  function playRevealSound(isShiny, rarity) {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    if (isShiny) {
      // Special shiny sound - sparkly ascending chime
      [0, 0.1, 0.2, 0.3].forEach((delay, i) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        const freq = 800 + i * 200;
        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + delay);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime + delay);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + delay + 0.4);

        oscillator.start(audioContext.currentTime + delay);
        oscillator.stop(audioContext.currentTime + delay + 0.4);
      });
    } else {
      // Regular reveal sound based on rarity
      const rarityFreqs = {
        'COMMON': 400,
        'UNCOMMON': 500,
        'RARE': 600,
        'EPIC': 700,
        'LEGENDARY': 900,
        'MYTHIC': 1200
      };

      const freq = rarityFreqs[rarity] || 400;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.6);
    }
  }

  async function handleOpenLootbox(lootboxType = 'normal', isFree = false) {
    if (!user) {
      toast.error("Please log in to open lootboxes!");
      return;
    }

    if (isOpening || isOpeningRef.current) return;
    isOpeningRef.current = true;

    // Free daily claim — server validates cooldown and grants boxes
    if (isFree) {
      // Optimistically add free lootboxes and set cooldown timestamp
      const prevStock = effectiveUserData?.lootboxStock || {};
      setUserData({
        lootboxStock: { ...prevStock, normal: (prevStock.normal || 0) + freeDailyBoxes },
        lastFreeLootbox: Date.now(),
      });
      setCanClaimDaily(false);
      toast.success(`You received ${freeDailyBoxes} FREE Normal Lootboxes!`);

      // Fire server call in background — UI is already updated optimistically
      isOpeningRef.current = false;
      const claimFree = httpsCallable(functions, 'claimFreeLootbox');
      claimFree().then(() => {
        // Refresh server data, then clear optimistic overlay so effectiveUserData
        // never briefly reverts to stale prop data (which could flash canClaimDaily=true)
        onUserDataChange().then(() => setLocalUserData(null));
      }).catch(async (err) => {
        // Roll back optimistic update — refresh server data first
        await onUserDataChange();
        setLocalUserData(null);
        setCanClaimDaily(true);
        const msg = err.message || 'Could not claim daily lootboxes.';
        toast.error(`${msg}`);
      });
      return;
    }

    // Optimistic check (server will enforce authoritatively)
    const isTestAccount = user.email === 'test@test.com';
    const hasUnlimitedGod = isTestAccount && lootboxType === 'god';
    const stock = effectiveUserData.lootboxStock || {};
    if (!hasUnlimitedGod && (stock[lootboxType] || 0) <= 0) {
      toast.warn(`No ${lootboxType} lootboxes to open!`);
      isOpeningRef.current = false;
      return;
    }

    // Optimistically decrement stock in local state so UI reflects it immediately
    if (!hasUnlimitedGod) {
      setUserData(prev => ({
        lootboxStock: {
          ...(effectiveUserData.lootboxStock || {}),
          ...(prev.lootboxStock || {}),
          [lootboxType]: Math.max(0, ((effectiveUserData.lootboxStock || {})[lootboxType] || 0) - 1),
        },
      }));
    }

    // Start animation immediately — server call runs concurrently.
    // Position 50 is off-screen during the scroll, so we slot the server
    // result in when it arrives with no visible swap.
    setWonItem(null);
    setCurrentLootboxType(lootboxType);
    setShowReward(false);
    setIsOpening(true);
    playOpenSound();
    playScrollSound();

    const ANIMATION_DURATION = 5000;
    const openBox = httpsCallable(functions, 'openLootbox');

    // Fire server call and animation timer concurrently
    const serverPromise = openBox({ lootboxType }).then(result => {
      const serverItem = result.data.item;
      // Slot the server result into position 50 while the strip is still scrolling
      setWonItem(serverItem);
      return serverItem;
    });

    // Wait for both the animation AND the server response
    let results;
    try {
      results = await Promise.all([
        serverPromise,
        new Promise(resolve => setTimeout(resolve, ANIMATION_DURATION)),
      ]);
    } catch (err) {
      // Server failed — roll back optimistic stock update and abort
      setIsOpening(false);
      isOpeningRef.current = false;
      setLocalUserData(null);
      await onUserDataChange();
      toast.error(err.message || 'Failed to open lootbox. Please try again.');
      return;
    }

    const item = results[0];

    // Wait one frame so React flushes the wonItem update into LootboxAnimation
    // before we unmount it. Without this, React 18 batching can merge setWonItem
    // (from the server .then()) with setIsOpening(false) into a single render,
    // causing the animation to unmount without ever showing the server result at
    // position 50 — the user sees the random placeholder item instead.
    // This mainly affects the first open (cold-start latency ≈ animation duration).
    await new Promise(r => requestAnimationFrame(r));

    playRevealSound(item.isShiny, item.rarity);

    if (item && item.rarity === 'NUCLEAR') {
      // Play the tactical nuke incoming callout first
      const nukeIncoming = new Audio('/Tactical Nuke Incoming Sound Effect.mp3');
      nukeIncoming.volume = 1.0;
      nukeIncoming.play().catch(err => console.log('Audio play failed:', err));

      // Launch fullscreen nuclear explosion overlay
      setShowNuclearExplosion(true);

      // Unmount animation FIRST so data refresh re-renders can't disturb it
      setIsOpening(false);
      isOpeningRef.current = false;

      // Refresh from server now that animation is gone
      setLocalUserData(null);
      await onUserDataChange();

      // Don't show reward modal yet — the explosion overlay will dismiss itself
      // and THEN we show the reward modal for maximum impact
      return;
    }

    // Unmount animation FIRST so data refresh re-renders can't disturb it
    setIsOpening(false);
    isOpeningRef.current = false;
    setShowReward(true);

    // Refresh from server now that animation is gone
    setLocalUserData(null);
    await onUserDataChange();
  }

  function closeRewardModal() {
    setShowReward(false);
    setWonItem(null);
  }

  function handleSellDuplicate(item) {
    // Close the reward modal
    closeRewardModal();
    // Show the listing modal for this specific item
    setItemToList(item);
    setShowListingModal(true);
  }

  async function handleQuickSell(item) {
    const price = item.rarity === 'COMMON' ? 0.5 : 1;
    await handleListItem(item, price, false, "crewmate");
    closeRewardModal();
  }

  async function handleListItem(item, price, auctionMode, currencyType, auctionDurationHours) {
    // Optimistic: close modal and show success immediately
    toast.success("Item listed successfully!");
    setShowListingModal(false);
    setItemToList(null);

    const createListingFn = httpsCallable(functions, 'createListing');
    createListingFn({ item, price, auctionMode, currencyType, auctionDurationHours }).then(() => {
      onUserDataChange();
    }).catch((error) => {
      console.error("Error listing item:", error);
      toast.error(error.message || "Failed to list item. Please try again.");
      onUserDataChange();
    });
  }

  async function handlePurchaseLootbox(lootboxType, quantity, currencyType) {
    if (!user || !effectiveUserData) {
      toast.error("Please log in to purchase lootboxes!");
      return;
    }

    if (isPurchasing) return;

    // Mirrored price table for optimistic UI update only (server is authoritative)
    const LOOTBOX_PRICES_CLIENT = isS2 ? {
      normal:   { 1: { crewmate: 1, imposter: null }, 10: { crewmate: 8, imposter: 2 } },
      shiny:    { 1: { crewmate: 3, imposter: null }, 10: { crewmate: 24, imposter: 6 } },
      stattrak: { 1: { crewmate: 3, imposter: null }, 10: { crewmate: 24, imposter: 6 } },
      vfxbox:   { 1: { crewmate: 10, imposter: 2.5 }, 10: { crewmate: 80, imposter: 20 } },
      elite:    { 1: { crewmate: 25, imposter: 6.25 } },
      god:      { 1: { crewmate: 100, imposter: 25 } },
    } : {
      normal:   { 1: { crewmate: 1, imposter: null }, 10: { crewmate: 8, imposter: 2 } },
      shiny:    { 1: { crewmate: 2, imposter: null }, 10: { crewmate: 16, imposter: 4 } },
      stattrak: { 1: { crewmate: 2, imposter: null }, 10: { crewmate: 16, imposter: 4 } },
      vfxbox:   { 1: { crewmate: 8, imposter: 2 }, 10: { crewmate: 64, imposter: 16 } },
      elite:    { 1: { crewmate: 20, imposter: 5 } },
      god:      { 1: { crewmate: 80, imposter: 20 } },
    };
    const tokenField = currencyType === 'crewmate' ? 'crewmateTokens' : 'imposterTokens';
    const optCost = (LOOTBOX_PRICES_CLIENT[lootboxType]?.[quantity]?.[currencyType]) || 0;

    // Check sufficient funds client-side before proceeding
    if (optCost > 0 && (effectiveUserData[tokenField] || 0) < optCost) {
      toast.error('Insufficient tokens');
      return;
    }

    setIsPurchasing(true);

    const boxNames = {
      normal: "Normal",
      shiny: "Shiny",
      stattrak: "StatTrak",
      vfxbox: "VFX",
      elite: "Elite",
      god: "GOD BOX"
    };

    // Optimistically update token balance, stock count, and show success immediately
    if (optCost > 0) {
      setUserData({
        [tokenField]: Math.max(0, (effectiveUserData[tokenField] || 0) - optCost),
        lootboxStock: {
          ...(effectiveUserData.lootboxStock || {}),
          [lootboxType]: ((effectiveUserData.lootboxStock || {})[lootboxType] || 0) + quantity,
        },
      });
    }
    toast.success(`Purchased ${quantity} ${boxNames[lootboxType]} lootbox${quantity > 1 ? 'es' : ''}!`);
    setIsPurchasing(false);

    // Fire server call in background — UI is already updated optimistically
    const purchase = httpsCallable(functions, 'purchaseLootbox');
    purchase({ lootboxType, quantity, currencyType }).then(() => {
      // Sync with server-confirmed data and clear optimistic overlay
      onUserDataChange().then(() => setLocalUserData(null));
    }).catch(async (err) => {
      // Roll back optimistic update on failure
      await onUserDataChange();
      setLocalUserData(null);
      toast.error(err.message || 'Purchase failed — balance has been restored.');
    });
  }

  if (!user) {
    return (
      <div className="v2-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-2)' }}>
          <div style={{ fontSize: 44, marginBottom: 14, opacity: 0.4 }}>🎁</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, letterSpacing: '0.12em', color: 'var(--text)' }}>
            LOOTBOX SYSTEM
          </div>
          <div style={{ marginTop: 8, fontSize: 13 }}>Please log in to open daily lootboxes and collect rewards</div>
        </div>
      </div>
    );
  }

  const lbTabs = LB_TABS.map(t =>
    t.id === 'inventory' ? { ...t, label: `🎒 Inventory (${inventory.length})` } : t
  );

  return (
    <div className="v2-page">
      <PgHeader title="Daily Lootbox" sub="Open boxes, build your collection, trade up" />

      <SubTabs tabs={lbTabs} active={selectedTab} onChange={setSelectedTab} />

      {selectedTab === "open" ? (
        <div className="lootbox-open-section">
          <div className="lootbox-status">
            <div className="status-row">
            </div>
            {effectiveUserData.lootboxStock && (
              <div className="lootbox-stock-display">
                <h3>Your Lootbox Inventory:</h3>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                  {effectiveUserData.lootboxStock.normal > 0 && (
                    <div style={{
                      padding: '8px 16px',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px'
                    }}>
                      🎁 Normal: {effectiveUserData.lootboxStock.normal}
                    </div>
                  )}
                  {effectiveUserData.lootboxStock.shiny > 0 && (
                    <div style={{
                      padding: '8px 16px',
                      background: 'var(--bg-card)',
                      border: '1px solid #FFD700',
                      borderRadius: '8px'
                    }}>
                      ✨ Shiny: {effectiveUserData.lootboxStock.shiny}
                    </div>
                  )}
                  {effectiveUserData.lootboxStock.stattrak > 0 && (
                    <div style={{
                      padding: '8px 16px',
                      background: 'var(--bg-card)',
                      border: '1px solid #FF6B35',
                      borderRadius: '8px'
                    }}>
                      📊 StatTrak: {effectiveUserData.lootboxStock.stattrak}
                    </div>
                  )}
                  {effectiveUserData.lootboxStock.vfxbox > 0 && (
                    <div style={{
                      padding: '8px 16px',
                      background: 'var(--bg-card)',
                      border: '1px solid #a78bfa',
                      borderRadius: '8px'
                    }}>
                      ✦ VFX: {effectiveUserData.lootboxStock.vfxbox}
                    </div>
                  )}
                  {effectiveUserData.lootboxStock.elite > 0 && (
                    <div style={{
                      padding: '8px 16px',
                      background: 'var(--bg-card)',
                      border: '1px solid #9B59B6',
                      borderRadius: '8px'
                    }}>
                      👑 Elite: {effectiveUserData.lootboxStock.elite}
                    </div>
                  )}
                  {effectiveUserData.lootboxStock.god > 0 && (
                    <div style={{
                      padding: '8px 16px',
                      background: 'var(--bg-card)',
                      border: '1px solid #FFD700',
                      borderRadius: '8px'
                    }}>
                      ⚡ GOD BOX: {effectiveUserData.lootboxStock.god}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {isS2 && (
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={() => setShowWorthInfo(!showWorthInfo)}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {showWorthInfo ? '▾' : '▸'} How is item worth calculated?
              </button>
              {showWorthInfo && (
                <div style={{
                  marginTop: '8px',
                  padding: '16px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  <div style={{ marginBottom: '12px', fontWeight: 'bold', color: 'var(--text-primary)' }}>Base Values by Rarity</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    {Object.entries(S2_ITEM_VALUES).filter(([r]) => r !== 'NUCLEAR').map(([rarity, value]) => {
                      const rarityInfo = LOOTBOX_RARITIES[rarity];
                      return (
                        <div key={rarity} style={{
                          padding: '4px 10px',
                          background: 'rgba(255,255,255,0.05)',
                          border: `1px solid ${rarityInfo?.color || '#888'}`,
                          borderRadius: '4px',
                          color: rarityInfo?.color || '#888'
                        }}>
                          {rarityInfo?.name || rarity}: ${value.toFixed(2)}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginBottom: '8px', fontWeight: 'bold', color: 'var(--text-primary)' }}>Multipliers</div>
                  <div style={{ color: 'var(--text-secondary)' }}>
                    <div>✨ <strong>Shiny</strong> — {S2_MODIFIER_MULTIPLIERS.shiny}x value</div>
                    <div>✦ <strong>VFX</strong> — {S2_MODIFIER_MULTIPLIERS.vfx}x value</div>
                    <div>📊 <strong>StatTrak</strong> — 1.5x + 0.5x per extra tracked stat</div>
                  </div>
                  <div style={{ marginTop: '8px', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '12px' }}>
                    Multipliers stack multiplicatively. e.g. a Shiny VFX Legendary = $5.00 × 2 × 5 = $50.00
                  </div>
                </div>
              )}
            </div>
          )}

          {isOpening ? (
            <LootboxAnimation items={getLootboxItems(isS2)} wonItem={wonItem} lootboxType={currentLootboxType} />
          ) : (
            <div className="lootbox-display">
              <div className="lootbox-3d">
                <div className="lootbox-box">
                  🎁
                </div>
              </div>
              <div className="lootbox-buttons">
                {/* Daily Free Lootbox Claim */}
                {canClaimDaily ? (
                  <button
                    className="btn-open-lootbox daily-claim"
                    onClick={() => handleOpenLootbox('normal', true)}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: '2px solid #FFD700',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    🎁 Claim {freeDailyBoxes} FREE Daily Lootboxes!
                  </button>
                ) : nextClaimTime && (
                  <div style={{
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    ⏰ Next free claim in: {Math.floor((nextClaimTime - Date.now()) / (1000 * 60 * 60))}h {Math.floor(((nextClaimTime - Date.now()) % (1000 * 60 * 60)) / (1000 * 60))}m
                  </div>
                )}

                {effectiveUserData.lootboxStock && effectiveUserData.lootboxStock.normal > 0 && (
                  <button
                    className="btn-open-lootbox"
                    onClick={() => handleOpenLootbox('normal', false)}
                  >
                    🎁 Open Normal Box ({effectiveUserData.lootboxStock.normal})
                  </button>
                )}

                {effectiveUserData.lootboxStock && effectiveUserData.lootboxStock.shiny > 0 && (
                  <button
                    className="btn-open-lootbox special"
                    onClick={() => handleOpenLootbox('shiny', false)}
                  >
                    ✨ Open Shiny Box ({effectiveUserData.lootboxStock.shiny})
                  </button>
                )}

                {effectiveUserData.lootboxStock && effectiveUserData.lootboxStock.stattrak > 0 && (
                  <button
                    className="btn-open-lootbox special"
                    onClick={() => handleOpenLootbox('stattrak', false)}
                  >
                    📊 Open StatTrak Box ({effectiveUserData.lootboxStock.stattrak})
                  </button>
                )}

                {effectiveUserData.lootboxStock && effectiveUserData.lootboxStock.vfxbox > 0 && (
                  <button
                    className="btn-open-lootbox special"
                    onClick={() => handleOpenLootbox('vfxbox', false)}
                    style={{ borderColor: '#a78bfa' }}
                  >
                    ✦ Open VFX Box ({effectiveUserData.lootboxStock.vfxbox})
                  </button>
                )}

                {effectiveUserData.lootboxStock && effectiveUserData.lootboxStock.elite > 0 && (
                  <button
                    className="btn-open-lootbox elite"
                    onClick={() => handleOpenLootbox('elite', false)}
                  >
                    👑 Open Elite Box ({effectiveUserData.lootboxStock.elite})
                  </button>
                )}

                {(effectiveUserData.lootboxStock && effectiveUserData.lootboxStock.god > 0) || (user && user.email === 'test@test.com') ? (
                  <button
                    className="btn-open-lootbox god"
                    onClick={() => handleOpenLootbox('god', false)}
                  >
                    ⚡ Open GOD BOX ({user && user.email === 'test@test.com' ? '∞' : effectiveUserData.lootboxStock.god})
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {/* Rarity Information */}
          <div className="rarity-info">
            <h3>Drop Rates</h3>
            <div className="rarity-grid">
              {Object.entries(getLootboxRarities(isS2)).map(([key, rarity]) => (
                <div key={key} className="rarity-card" style={{ borderColor: rarity.color }}>
                  <div className="rarity-name" style={{ color: rarity.color }}>
                    {rarity.name}
                  </div>
                  <div className="rarity-rate">{rarity.dropRate}%</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '8px' }}>
              <h4 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--text-primary)' }}>Special Properties</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '14px' }}>
                <div style={{ padding: '12px', background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 'bold', color: '#FFD700', marginBottom: '4px' }}>✨ Shiny</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {isS2 ? (
                      <>• Common–Epic: 5%<br />• Legendary: 10%<br />• Mythic: 20%<br />• Galaxy: 50%<br />• Shiny Box: ×5 multiplier</>
                    ) : (
                      <>• Normal: 5%<br />• Shiny Box: 50%<br />• MYTHIC/GALAXY/NUCLEAR: 100%</>
                    )}
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(255, 140, 0, 0.05)', border: '1px solid rgba(255, 140, 0, 0.2)', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 'bold', color: '#FF8C00', marginBottom: '4px' }}>📊 StatTrak</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    • Normal: 10%<br />
                    • StatTrak Box: 100%<br />
                    • Possible stats: Kills, Tasks Completed, Crewmate Wins, Imposter Wins, Wins, Crewmate Losses, Imposter Losses, Losses, Times Imposter, Times Crewmate, Games Played
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(167, 139, 250, 0.05)', border: '1px solid rgba(167, 139, 250, 0.3)', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 'bold', color: '#a78bfa', marginBottom: '4px' }}>✦ VFX</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    • Any item: 1% (Normal/Shiny/StatTrak/Elite/God)<br />
                    • VFX Box: 50%<br />
                    • {VFX_EFFECTS.length} possible effects: {VFX_EFFECTS.map(e => e.name).join(', ')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* All Available Items */}
          <div className="all-items-section">
            <h3>All Available Items</h3>
            {Object.entries(getLootboxRarities(isS2)).reverse().map(([rarityKey, rarityData]) => {
              const itemsOfRarity = getLootboxItems(isS2).filter(item => item.rarity === rarityKey);
              if (itemsOfRarity.length === 0) return null;

              return (
                <div key={rarityKey} className="all-items-rarity-section">
                  <h4 style={{ color: rarityData.color }}>
                    {rarityData.name} ({itemsOfRarity.length} items - {rarityData.dropRate}% drop rate)
                  </h4>
                  <div className="all-items-grid">
                    {itemsOfRarity.map((item) => (
                      <div
                        key={item.id}
                        className={`all-items-card ${rarityData.glow ? 'glow' : ''}`}
                        style={{
                          borderColor: rarityData.color,
                          boxShadow: rarityData.glow ? `0 0 10px ${rarityData.color}` : 'none'
                        }}
                      >
                        <div className="item-icon"><ItemIcon item={item} size={48} /></div>
                        <div className={`item-name ${rarityData.rainbow ? 'rainbow-glow' : ''}`} style={{ color: rarityData.color }}>
                          {item.name}
                        </div>
                        <div className="item-description">{item.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : selectedTab === "shop" ? (
        <div className="lootbox-shop-section">
          <h2>🛒 Lootbox Shop</h2>
          <p className="shop-subtitle">Purchase lootboxes with your tokens</p>

          <div className="shop-offers">
            {/* Normal Lootbox - 1x */}
            <div className="shop-offer-card">
              <div className="offer-icon">🎁</div>
              <h3>Normal Lootbox</h3>
              <p className="offer-description">Standard drop rates</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('normal', 1, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < 1}
                >
                  Buy for 1 🔵
                </button>
              </div>
            </div>

            {/* Normal Lootbox - 10x */}
            <div className="shop-offer-card bundle">
              <div className="bundle-badge">20% OFF</div>
              <div className="offer-icon">🎁✨</div>
              <h3>10 Normal Lootboxes</h3>
              <p className="offer-description">Standard drop rates (bulk)</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('normal', 10, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < 8}
                >
                  Buy for 8 🔵
                  <span className="price-savings">(Save 2 🔵)</span>
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('normal', 10, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < 2}
                >
                  Buy for 2 🔴
                </button>
              </div>
            </div>

            {/* Shiny Lootbox - 1x */}
            <div className="shop-offer-card special">
              <div className="offer-icon">✨</div>
              <h3>Shiny Lootbox</h3>
              <p className="offer-description">{isS2 ? '25% shiny chance!' : '50% shiny chance!'}</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('shiny', 1, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.shiny[1].crew}
                >
                  Buy for {shopPrices.shiny[1].crew} 🔵
                </button>
              </div>
            </div>

            {/* Shiny Lootbox - 10x */}
            <div className="shop-offer-card bundle special">
              <div className="bundle-badge">20% OFF</div>
              <div className="offer-icon">✨💎</div>
              <h3>10 Shiny Lootboxes</h3>
              <p className="offer-description">{isS2 ? '25% shiny chance (bulk)' : '50% shiny chance (bulk)'}</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('shiny', 10, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.shiny[10].crew}
                >
                  Buy for {shopPrices.shiny[10].crew} 🔵
                  <span className="price-savings">(Save {shopPrices.shiny[1].crew * 10 - shopPrices.shiny[10].crew} 🔵)</span>
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('shiny', 10, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < shopPrices.shiny[10].imp}
                >
                  Buy for {shopPrices.shiny[10].imp} 🔴
                </button>
              </div>
            </div>

            {/* StatTrak Lootbox - 1x */}
            <div className="shop-offer-card special">
              <div className="offer-icon">📊</div>
              <h3>StatTrak Lootbox</h3>
              <p className="offer-description">100% StatTrak chance!</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('stattrak', 1, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.stattrak[1].crew}
                >
                  Buy for {shopPrices.stattrak[1].crew} 🔵
                </button>
              </div>
            </div>

            {/* StatTrak Lootbox - 10x */}
            <div className="shop-offer-card bundle special">
              <div className="bundle-badge">20% OFF</div>
              <div className="offer-icon">📊🎯</div>
              <h3>10 StatTrak Lootboxes</h3>
              <p className="offer-description">100% StatTrak chance (bulk)</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('stattrak', 10, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.stattrak[10].crew}
                >
                  Buy for {shopPrices.stattrak[10].crew} 🔵
                  <span className="price-savings">(Save {shopPrices.stattrak[1].crew * 10 - shopPrices.stattrak[10].crew} 🔵)</span>
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('stattrak', 10, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < shopPrices.stattrak[10].imp}
                >
                  Buy for {shopPrices.stattrak[10].imp} 🔴
                </button>
              </div>
            </div>

            {/* VFX Box - 1x */}
            <div className="shop-offer-card vfxbox-card">
              <div className="offer-icon"><span className="vfx-rainbow vfx-shine" style={{ display: 'inline-block' }}>✦</span></div>
              <h3><span className="vfx-hueShift" style={{ display: 'inline-block' }}>VFX Box</span></h3>
              <p className="offer-description"><span className="vfx-rgbShift" style={{ display: 'inline-block' }}>50% chance to pull a VFX item!</span></p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('vfxbox', 1, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.vfxbox[1].crew}
                >
                  Buy for {shopPrices.vfxbox[1].crew} 🔵
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('vfxbox', 1, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < shopPrices.vfxbox[1].imp}
                >
                  Buy for {shopPrices.vfxbox[1].imp} 🔴
                </button>
              </div>
            </div>

            {/* VFX Box - 10x */}
            <div className="shop-offer-card bundle vfxbox-card">
              <div className="bundle-badge">20% OFF</div>
              <div className="offer-icon">
                <span className="vfx-rainbow vfx-shine" style={{ display: 'inline-block' }}>✦</span>
                <span className="vfx-rainbow vfx-shine" style={{ display: 'inline-block', marginLeft: '2px' }}>✦</span>
              </div>
              <h3><span className="vfx-hueShift" style={{ display: 'inline-block' }}>10 VFX Boxes</span></h3>
              <p className="offer-description"><span className="vfx-rgbShift" style={{ display: 'inline-block' }}>50% VFX chance (bulk)</span></p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('vfxbox', 10, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.vfxbox[10].crew}
                >
                  Buy for {shopPrices.vfxbox[10].crew} 🔵
                  <span className="price-savings">(Save {shopPrices.vfxbox[1].crew * 10 - shopPrices.vfxbox[10].crew} 🔵)</span>
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('vfxbox', 10, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < shopPrices.vfxbox[10].imp}
                >
                  Buy for {shopPrices.vfxbox[10].imp} 🔴
                  <span className="price-savings">(Save {shopPrices.vfxbox[1].imp * 10 - shopPrices.vfxbox[10].imp} 🔴)</span>
                </button>
              </div>
            </div>

            {/* Elite Lootbox */}
            <div className="shop-offer-card elite">
              <div className="offer-icon">👑</div>
              <h3>Elite Lootbox</h3>
              <p className="offer-description">{isS2 ? '55% Epic, 30% Legendary, 10% Mythic, 5% Galaxy!' : '55% Epic, 30% Legendary, 10% Mythic, 4% Galaxy, 1% NUCLEAR!'}</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('elite', 1, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.elite[1].crew}
                >
                  Buy for {shopPrices.elite[1].crew} 🔵
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('elite', 1, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < shopPrices.elite[1].imp}
                >
                  Buy for {shopPrices.elite[1].imp} 🔴
                </button>
              </div>
            </div>

            {/* GOD BOX */}
            <div className="shop-offer-card god">
              <div className="offer-icon">⚡</div>
              <h3>GOD BOX</h3>
              <p className="offer-description">{isS2 ? '5% Common, 40% Legendary, 45% Mythic, 10% Galaxy!' : '5% Common, 35% Legendary, 30% Mythic, 20% Galaxy, 10% NUCLEAR!'}</p>
              <div className="offer-prices">
                <button
                  className="btn-shop-purchase crew"
                  onClick={() => handlePurchaseLootbox('god', 1, "crewmate")}
                  disabled={(effectiveUserData?.crewmateTokens || 0) < shopPrices.god[1].crew}
                >
                  Buy for {shopPrices.god[1].crew} 🔵
                </button>
                <button
                  className="btn-shop-purchase impostor"
                  onClick={() => handlePurchaseLootbox('god', 1, "imposter")}
                  disabled={(effectiveUserData?.imposterTokens || 0) < shopPrices.god[1].imp}
                >
                  Buy for {shopPrices.god[1].imp} 🔴
                </button>
              </div>
            </div>
          </div>

          <div className="shop-info">
            <h3>💡 How to Earn Tokens</h3>
            <div className="earn-methods">
              <div className="earn-card">
                <div className="earn-icon">🔵</div>
                <div className="earn-text">
                  <strong>Crewmate Tokens:</strong> Win games as a crewmate
                </div>
              </div>
              <div className="earn-card">
                <div className="earn-icon">🔴</div>
                <div className="earn-text">
                  <strong>Imposter Tokens:</strong> Win games as an imposter
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : selectedTab === "inventory" ? (
        <LootboxInventory inventory={inventory} user={user} userData={effectiveUserData} onUserDataChange={onUserDataChange} />
      ) : selectedTab === "tradeup" ? (
        <TradeUpSection inventory={inventory} user={user} userData={effectiveUserData} onUserDataChange={onUserDataChange} isS2={isS2} />
      ) : selectedTab === "collection" ? (
        <CollectionTracker inventory={inventory} user={user} userData={effectiveUserData} onUserDataChange={onUserDataChange} isS2={isS2} />
      ) : null}

      {/* NUCLEAR EXPLOSION FULLSCREEN OVERLAY */}
      {showNuclearExplosion && (
        <NuclearExplosionOverlay onComplete={() => {
          setShowNuclearExplosion(false);
          setShowReward(true);
        }} />
      )}

      {/* Reward Modal */}
      {showReward && wonItem && (
        <LootboxRewardModal
          item={wonItem}
          onClose={closeRewardModal}
          inventory={inventory}
          onSellDuplicate={handleSellDuplicate}
          onQuickSell={handleQuickSell}
          isS2={isS2}
        />
      )}

      {/* Listing Modal for Selling Duplicates */}
      {showListingModal && itemToList && (
        <ListingModal
          item={itemToList}
          onClose={() => {
            setShowListingModal(false);
            setItemToList(null);
          }}
          onListItem={handleListItem}
        />
      )}
    </div>
  );
}

export default LootboxPage;
